import React from "react";
import { Box, Container, Stack, Grid } from "@mui/material";
import { useCart } from "../../../context/CartContext";
import { useNavigate, useLocation } from "react-router-dom";
import colorConfigs from "../../../configs/colorConfigs";
import { PATH_NAME } from '../../../configs/pathName';
import { createOrder } from "../../../api/OrderAPI";
import { ICreateOrderRequest, IOrderItem, IPayment } from "../../../models/IWallet";
import { requestStripePayment, requestStripePaymentGuest, startTransaction } from "../../../api/WalletAPI";
import { IStripePaymentPayload } from "../../../models/IWallet";
import { toast } from "../../common/SnackBar/snackBar";
import { CheckoutHeader } from "./CheckoutHeader/checkoutHeader";
import { OrderSummary } from "./CheckoutLayout/orderSummary";
import { PaymentMethodSection } from "./CheckoutLayout/paymentMethodSection";
import { OrderNotifications } from "./orderNotification";
import ShippingInformationForm from "./CheckoutLayout/shippingInformationForm";
import { useAppSelector } from "../../../redux/features/redux-hooks";
import { startGuestTransaction } from "../../../api/guestTransactionAPI";

const CheckoutPage = () => {
  const { cart, totalPrice, clearCart, deliveryDetails } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [orderLoading, setOrderLoading] = React.useState(false);
  const [paymentMethod, setPaymentMethod] = React.useState("stripe");
  const [orderSuccess, setOrderSuccess] = React.useState(false);
  const [orderError, setOrderError] = React.useState<string | null>(null);

  const deliveryDate = location.state?.deliveryDate || null;
  const deliveryTime = location.state?.deliveryTime || null;

  const handlePaymentMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentMethod((event.target as HTMLInputElement).value);
  };

  const basicUserInfo = useAppSelector((state) => state.authState.basicUserInfo);
  
  React.useEffect (() => {
    const checkTransaction = async () => {
      const guestTransaction = localStorage.getItem('guest_transaction');
      if (guestTransaction) {
        try {
          const parsedTransaction = JSON.parse(guestTransaction);
          if (parsedTransaction.transaction_ref) {
            // Here you might want to verify with your backend
            // if the transaction is still valid
            // For now, we'll just create a new one if one exists
            await startNewTransaction();
          }
        } catch (e) {
          console.error('Error checking transaction:', e);
          await startNewTransaction();
        }
      } else {
        await startNewTransaction();
      }
    };
  
    checkTransaction();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const startNewTransaction = async () => {
    try {
      let transaction;
      
      if (basicUserInfo) {
        // User is logged in - use startTransaction API
        transaction = await startTransaction();
      } else {
        // User is not logged in - use startGuestTransaction API
        transaction = await startGuestTransaction();
      }
  
      localStorage.setItem('guest_transaction', JSON.stringify({
        transaction_id: transaction.id,
        transaction_ref: transaction.transaction_ref,
        state: basicUserInfo ? 'user-order' : 'guest-order',
        deliveryDate,
        deliveryDetails
      }));
      
      return transaction.transaction_ref;
    } catch (error) {
      console.error('Failed to start new transaction:', error);
      toast.error('Failed to initialize checkout. Please try again.');
      throw error;
    }
  };
  
  const handleStripePayment = async () => {
    try {
      setOrderLoading(true);
      
      let invoiceNo = '';
      let shouldCreateNewTransaction = false;
  
      // Check if transaction exists and is valid
      const storedTransaction = localStorage.getItem('guest_transaction');
      if (storedTransaction) {
        try {
          const parsedTransaction = JSON.parse(storedTransaction);
          if (parsedTransaction.transaction_ref) {
            // Verify if transaction state matches current user state
            const expectedState = basicUserInfo ? 'user-order' : 'guest-order';
            if (parsedTransaction.state !== expectedState) {
              shouldCreateNewTransaction = true;
            }
          }
        } catch (e) {
          shouldCreateNewTransaction = true;
        }
      } else {
        shouldCreateNewTransaction = true;
      }
  
      // Create new transaction if needed
      if (shouldCreateNewTransaction) {
        invoiceNo = await startNewTransaction();
      } else {
        const parsedTransaction = JSON.parse(storedTransaction!);
        invoiceNo = parsedTransaction.transaction_ref;
      }
  
      const stripePayload: IStripePaymentPayload = {
        invoiceNo: invoiceNo,
        description: "Order Payment",
        amount: totalPrice,
        order_type: basicUserInfo ? "user" : "guest"
      };
      
      let stripeResponse;
      if (basicUserInfo) {
        stripeResponse = await requestStripePayment(stripePayload);
      } else {
        stripeResponse = await requestStripePaymentGuest(stripePayload);
      }
      
      const url = stripeResponse.result?.url;
      
      if (stripeResponse && url) {
        window.location.href = url;
      } else {
        toast.error("Something went wrong!");
        setOrderLoading(false);
      }
    } catch (error) {
      setOrderLoading(false);
      toast.error("Something went wrong!");
    }
  };
  
  const handlePlaceOrder = async () => {
    
  
    if (!deliveryDetails) {
      toast.error("Delivery details are required");
      return;
    }

    // if (deliveryDetails.self_collect && !deliveryDetails.pickup_location) {
    //   toast.error("Please select a pickup location");
    //   return;
    // }
  
    // Handle Stripe payment
    if (paymentMethod === "stripe") {
      try {
        // Ensure we have a fresh transaction
        await startNewTransaction();
        handleStripePayment();
      } catch (error) {
        setOrderLoading(false);
        toast.error("Failed to initialize payment. Please try again.");
      }
      return;
    }
  
    try {
      setOrderLoading(true);
      
      // Prepare order items
      const orderItems: IOrderItem[] = cart.items.map(item => ({
        sku_sku: item.sku_sku,
        sku_id: 0,
        price: item.price,
        quantity: item.quantity,
        item_total: item.price * item.quantity,
        sku_name: item.sku_name
      }));
  
      // Prepare payment
      const payments: IPayment[] = [
        {
          type: paymentMethod,
          amount: totalPrice
        }
      ];
    
      const orderData: ICreateOrderRequest = {
        order_ref: '',
        subtotal: totalPrice,
        payment_amount: totalPrice,
        items: orderItems,
        payments: payments,
        delivery_details: deliveryDetails
      };
  
      const order = await createOrder(orderData);
            
      clearCart();
      
      setOrderSuccess(true);
      
      navigate(PATH_NAME.PAYMENT_SUCCESS, {
        state: {
          orderId: order.id,
          orderRef: ''
        }
      });
      
    } catch (error) {
      setOrderError("Failed to create order. Please try again.");
      toast.error("Failed to create order. Please try again.");
    } finally {
      setOrderLoading(false);
    }
  };

  // React.useEffect(() => {
  //   const timer = setTimeout(() => setLoading(false), 0);
  //   return () => clearTimeout(timer);
  // }, []);

  // if (loading) {
  //   return (
  //     <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
  //       <CircularProgress />
  //     </Container>
  //   );
  // }

  return (
    <Box sx={{ background: colorConfigs.secondary }}>
      <Container sx={{ py: 4 }}>
        <Stack spacing={4}>
          <CheckoutHeader />
          
          <Grid container item xs={12}>
            {/* Left column - Shipping and Payment */}
            <Grid item xs={12} md={7}>
              <ShippingInformationForm />
            
              <PaymentMethodSection
                paymentMethod={paymentMethod}
                onPaymentMethodChange={handlePaymentMethodChange}
              />
            </Grid>

            {/* Right column - Order Summary */}
            <Grid item xs={12} md={5}>
              <OrderSummary
                totalPrice={totalPrice}
                cartItems={cart.items}
                isOrderLoading={orderLoading}
                isDisabled={cart.items.length === 0 }
                onPlaceOrder={handlePlaceOrder}
                deliveryDate={deliveryDate}
                deliveryTime={deliveryTime}
              />
            </Grid>
          </Grid>
        </Stack>
      </Container>

      <OrderNotifications
        orderSuccess={orderSuccess}
        orderError={orderError}
        onCloseSuccess={() => setOrderSuccess(false)}
        onCloseError={() => setOrderError(null)}
      />
    </Box>
  );
};

export default CheckoutPage;